import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getStravaToken } from './AuthSlice';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

function StravaSuccess() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const stravaAuth = useSelector((state) => state.auth.connections.strava);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Check for authorization code in URL
        const code = searchParams.get('code');
        const error = searchParams.get('error');

        if (error) {
            setError('Authorization was denied or failed');
            setIsLoading(false);
            return;
        }

        if (code) {
            // Exchange code for token
            dispatch(getStravaToken({ code }))
                .then((result) => {
                    if (result.type === 'auth/getStravaToken/fulfilled') {
                        // Success - redirect to home
                        navigate('/', { replace: true });
                    } else {
                        setError('Failed to exchange authorization code');
                        setIsLoading(false);
                    }
                })
                .catch((err) => {
                    console.error('Strava token exchange failed:', err);
                    setError('Failed to exchange authorization code');
                    setIsLoading(false);
                });
        } else {
            setError('No authorization code received');
            setIsLoading(false);
        }
    }, [searchParams, dispatch, navigate]);

    useEffect(() => {
        // If Strava auth is present, redirect to home
        if (stravaAuth) {
            navigate('/', { replace: true });
        }
    }, [stravaAuth, navigate]);

    // Show loading state while waiting for Strava auth
    if (isLoading) {
        return (
            <Container maxWidth="sm">
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 'calc(100vh - 64px)',
                    textAlign: 'center',
                    py: 4
                }}>
                    <CircularProgress size={60} sx={{ mb: 3 }} />
                    <Typography variant="h5" gutterBottom>
                        Connecting to Strava
                    </Typography>
                    <Typography color="text.secondary">
                        Please wait while we complete your authentication...
                    </Typography>
                </Box>
            </Container>
        );
    }

    // Error state
    return (
        <Container maxWidth="sm">
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 64px)',
                textAlign: 'center',
                py: 4
            }}>
                <Typography variant="h5" gutterBottom color="error">
                    Strava Authentication Failed
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {error || 'There was a problem connecting to Strava. Please try again.'}
                </Typography>
                <Button
                    variant="contained"
                    component={Link}
                    to="/"
                    sx={{ minWidth: 150 }}
                >
                    Go Home
                </Button>
            </Box>
        </Container>
    );
}

export default StravaSuccess;
